import { useEffect, useState } from 'react';
import ServiceList from '../components/ServiceList';
import Calendar from '../components/Calendar';
import ReservationForm from '../components/ReservationForm';
import Summary from '../components/Summary';
import { createReservation } from '../utils/api';
import { toApiDate } from '../utils/date';
import { executeRecaptcha } from '../config/recaptcha';
import './ReservationPage.css';

const STEPS = {
  SERVICE: 1,
  DATETIME: 2,
  CONTACT: 3,
  SUMMARY: 4,
  SUCCESS: 5,
};

const ReservationPage = () => {
  const [currentStep, setCurrentStep] = useState(STEPS.SERVICE);
  const [maxReachedStep, setMaxReachedStep] = useState(STEPS.SERVICE);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reservationData, setReservationData] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentStep]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
  };

  const handleContactSubmit = (data) => {
    setContactInfo(data);
    setCurrentStep(STEPS.SUMMARY);
    setMaxReachedStep(Math.max(maxReachedStep, STEPS.SUMMARY));
  };

  const goToStep = (step) => {
    if (step <= maxReachedStep) {
      setCurrentStep(step);
    }
  };

  const handleNextStep = (nextStep) => {
    setCurrentStep(nextStep);
    setMaxReachedStep(Math.max(maxReachedStep, nextStep));
  };

  const handleBackStep = () => {
    if (currentStep > STEPS.SERVICE) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleEdit = (section) => {
    switch (section) {
      case 'service':
        setCurrentStep(STEPS.SERVICE);
        break;
      case 'datetime':
        setCurrentStep(STEPS.DATETIME);
        break;
      case 'cele_meno':
      case 'email':
      case 'telefon':
      case 'adresa':
      case 'poznamka':
        setCurrentStep(STEPS.CONTACT);
        setTimeout(() => {
          const input = document.getElementById(section);
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        break;
      default:
        setCurrentStep(STEPS.CONTACT);
    }
  };

  const handleConfirmReservation = async () => {
    try {
      setLoading(true);
      setError(null);

      const recaptchaToken = await executeRecaptcha('reservation');
      if (!recaptchaToken) {
        setError('reCAPTCHA validation failed. Please refresh the page and try again.');
        setLoading(false);
        return;
      }

      const reservationPayload = {
        service_id: selectedService.id,
        datum: toApiDate(selectedDate),
        cas: selectedTime,
        cele_meno: contactInfo.cele_meno,
        email: contactInfo.email,
        telefon: contactInfo.telefon,
        adresa: contactInfo.adresa || '',
        poznamka: contactInfo.poznamka || '',
        recaptcha_token: recaptchaToken,
      };

      const response = await createReservation(reservationPayload);

      if (response.data.success) {
        setReservationData(response.data.data);
        setCurrentStep(STEPS.SUCCESS);
      } else {
        setError(response.data.message || 'Nepodarilo sa vytvoriť rezerváciu');
      }
    } catch (err) {
      console.error('Reservation error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).join(', ');
        setError(errorMessages);
      } else {
        setError(
          err.response?.data?.message ||
            'Nastala chyba pri vytváraní rezervácie. Skúste to prosím neskôr.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewReservation = () => {
    window.location.href = '/rezervacia';
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SERVICE:
        return (
          <ServiceList
            onSelect={handleServiceSelect}
            onNext={() => handleNextStep(STEPS.DATETIME)}
            selectedService={selectedService}
          />
        );

      case STEPS.DATETIME:
        return (
          <Calendar
            selectedService={selectedService}
            onDateTimeSelect={handleDateTimeSelect}
            onBack={handleBackStep}
            onNext={() => handleNextStep(STEPS.CONTACT)}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
          />
        );

      case STEPS.CONTACT:
        return (
          <ReservationForm
            onSubmit={handleContactSubmit}
            onBack={handleBackStep}
            initialData={contactInfo}
          />
        );

      case STEPS.SUMMARY:
        return (
          <Summary
            service={selectedService}
            date={selectedDate}
            time={selectedTime}
            contactInfo={contactInfo}
            onEdit={handleEdit}
            onConfirm={handleConfirmReservation}
            onBack={handleBackStep}
            loading={loading}
            error={error}
          />
        );

      case STEPS.SUCCESS:
        return (
            <div className="success-container">
              <div className="success-icon">✓</div>

              <h2>Rezervácia bola úspešná</h2>

              <p className="success-message">
                Na váš email sme vám poslali potvrdenie so všetkými detailmi rezervácie. 
                Ak ho nenájdete, prosím skontrolujte aj priečinok Spam.
              </p>

              {reservationData?.rezervacne_id && (
                <p className="success-message">
                  Číslo vašej rezervácie je: <strong>{reservationData.rezervacne_id}</strong>
                </p>
              )}

              <button onClick={handleNewReservation} className="btn btn-primary">
                Vytvoriť novú rezerváciu
              </button>
            </div>
          );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case STEPS.SERVICE:
        return 'Vyberte službu';
      case STEPS.DATETIME:
        return 'Vyberte dátum a čas';
      case STEPS.CONTACT:
        return 'Kontaktné údaje';
      case STEPS.SUMMARY:
        return 'Rekapitulácia';
      case STEPS.SUCCESS:
        return 'Hotovo!';
      default:
        return '';
    }
  };

  return (
    <div className="reservation-page">
      <div className="container">
        {currentStep !== STEPS.SUCCESS && (
          <>
            {/* Horizontal Step Progress Bar */}
            <div className="progress-steps">
              <div className="step-item">
                <div 
                  className={`step-circle ${currentStep >= STEPS.SERVICE ? 'active' : ''} ${currentStep > STEPS.SERVICE ? 'completed' : ''} ${STEPS.SERVICE <= maxReachedStep ? 'clickable' : ''}`}
                  onClick={() => goToStep(STEPS.SERVICE)}
                  role="button"
                  tabIndex={STEPS.SERVICE <= maxReachedStep ? 0 : -1}
                >
                  <span>1</span>
                </div>
                <div className="step-label">Služba</div>
              </div>
              <div className={`step-line ${currentStep > STEPS.SERVICE ? 'active' : ''}`}></div>
              
              <div className="step-item">
                <div 
                  className={`step-circle ${currentStep >= STEPS.DATETIME ? 'active' : ''} ${currentStep > STEPS.DATETIME ? 'completed' : ''} ${STEPS.DATETIME <= maxReachedStep ? 'clickable' : ''}`}
                  onClick={() => goToStep(STEPS.DATETIME)}
                  role="button"
                  tabIndex={STEPS.DATETIME <= maxReachedStep ? 0 : -1}
                >
                  <span>2</span>
                </div>
                <div className="step-label">Termín</div>
              </div>
              <div className={`step-line ${currentStep > STEPS.DATETIME ? 'active' : ''}`}></div>
              
              <div className="step-item">
                <div 
                  className={`step-circle ${currentStep >= STEPS.CONTACT ? 'active' : ''} ${currentStep > STEPS.CONTACT ? 'completed' : ''} ${STEPS.CONTACT <= maxReachedStep ? 'clickable' : ''}`}
                  onClick={() => goToStep(STEPS.CONTACT)}
                  role="button"
                  tabIndex={STEPS.CONTACT <= maxReachedStep ? 0 : -1}
                >
                  <span>3</span>
                </div>
                <div className="step-label">Kontakt</div>
              </div>
              <div className={`step-line ${currentStep > STEPS.CONTACT ? 'active' : ''}`}></div>
              
              <div className="step-item">
                <div 
                  className={`step-circle ${currentStep >= STEPS.SUMMARY ? 'active' : ''} ${STEPS.SUMMARY <= maxReachedStep ? 'clickable' : ''}`}
                  onClick={() => goToStep(STEPS.SUMMARY)}
                  role="button"
                  tabIndex={STEPS.SUMMARY <= maxReachedStep ? 0 : -1}
                >
                  <span>4</span>
                </div>
                <div className="step-label">Zhrnutie</div>
              </div>
            </div>

            <div className="page-header">
              <h1>{getStepTitle()}</h1>
            </div>
          </>
        )}

        <div className="reservation-content">{renderStep()}</div>
      </div>
    </div>
  );
};

export default ReservationPage;
